import React from "react";
// @ts-ignore: no types, remove this compontent in the future
import createDetectElementResize from "../vendor/detectElementResize";

interface Size {
  height: number;
  width: number;
}

interface AutoSizerProps {
  /** Function responsible for rendering children.*/
  children: (size: Size) => React.ReactNode;

  /** Default height to use for initial render; useful for SSR */
  defaultHeight?: number;

  /** Default width to use for initial render; useful for SSR */
  defaultWidth?: number;

  /** Disable dynamic :height property */
  disableHeight?: boolean;

  /** Disable dynamic :width property */
  disableWidth?: boolean;

  /** Nonce of the inlined stylesheet for Content Security Policy */
  nonce?: string;

  /** Callback to be invoked on-resize */
  onResize?: (size: Size) => void;
}

type State = {
  height: number;
  width: number;
};

type ResizeHandler = (element: HTMLElement, onResize: () => void) => void;

type DetectElementResize = {
  addResizeListener: ResizeHandler;
  removeResizeListener: ResizeHandler;
};

declare global {
  interface Window {
    HTMLElement: HTMLElement;
  }
}

/**
 * [AutoSizer] automatically resizes to available space, passing the width and height to its children.
 */
export function AutoSizer(props: AutoSizerProps) {
  const {
    nonce,
    children,
    onResize = () => {},
    disableHeight = false,
    disableWidth = false,
    defaultHeight = 0,
    defaultWidth = 0,
  } = props;
  const parentNodeRef = React.useRef<HTMLElement | null>(null);
  const autoSizerRef = React.useRef<HTMLDivElement | null>(null);
  const windowRef = React.useRef<Window | undefined | null>(null);
  const detectElementResizeRef = React.useRef<DetectElementResize | null>(null);
  const [state, setState] = React.useState<State>({
    height: defaultHeight,
    width: defaultWidth,
  });

  const handleResize = React.useCallback(() => {
    if (parentNodeRef.current) {
      // Guard against AutoSizer component being removed from the DOM immediately after being added.
      // This can result in invalid style values which can result in NaN values if we don't handle them.
      // See issue #150 for more context.

      const height = parentNodeRef.current.offsetHeight || 0;
      const width = parentNodeRef.current.offsetWidth || 0;

      const win = windowRef.current || window;
      const style = win.getComputedStyle(parentNodeRef.current) || {};
      const paddingLeft = parseInt(style.paddingLeft, 10) || 0;
      const paddingRight = parseInt(style.paddingRight, 10) || 0;
      const paddingTop = parseInt(style.paddingTop, 10) || 0;
      const paddingBottom = parseInt(style.paddingBottom, 10) || 0;

      const newHeight = height - paddingTop - paddingBottom;
      const newWidth = width - paddingLeft - paddingRight;

      if (
        (!disableHeight && state.height !== newHeight) ||
        (!disableWidth && state.width !== newWidth)
      ) {
        setState({
          height: height - paddingTop - paddingBottom,
          width: width - paddingLeft - paddingRight,
        });

        onResize({ height, width });
      }
    }
  }, [state, disableHeight, disableWidth, onResize]);

  React.useEffect(() => {
    if (
      autoSizerRef.current?.parentNode?.ownerDocument?.defaultView &&
      autoSizerRef.current.parentNode instanceof
        // @ts-ignore
        autoSizerRef.current.parentNode.ownerDocument.defaultView.HTMLElement
    ) {
      // Delay access of parentNode until mount.
      // This handles edge-cases where the component has already been unmounted before its ref has been set,
      // As well as libraries like react-lite which have a slightly different lifecycle.
      parentNodeRef.current = autoSizerRef.current.parentNode as HTMLElement;
      windowRef.current =
        autoSizerRef.current?.parentNode?.ownerDocument?.defaultView;

      // Defer requiring resize handler in order to support server-side rendering.
      // See issue #41
      detectElementResizeRef.current = createDetectElementResize(
        nonce,
        windowRef.current
      );
      detectElementResizeRef.current?.addResizeListener(
        parentNodeRef.current,
        handleResize
      );

      handleResize();
    }

    return () => {
      if (detectElementResizeRef.current && parentNodeRef.current) {
        detectElementResizeRef.current.removeResizeListener(
          parentNodeRef.current,
          handleResize
        );
      }
    };
  }, [nonce, handleResize]);

  const outerStyle: React.CSSProperties = { overflow: "visible" };
  const childParams: Size = { width: 0, height: 0 };

  if (!disableHeight) {
    outerStyle.height = 0;
    childParams.height = state.height;
  }

  if (!disableWidth) {
    outerStyle.width = 0;
    childParams.width = state.width;
  }

  return (
    <div data-component="AutoSizer" ref={autoSizerRef} style={outerStyle}>
      {children(childParams)}
    </div>
  );
}
